import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { getDashboardData, getCountdown } from '../../services/dashboardService'
import { supabase } from '../../config/supabase'
import InfoButton from '../../components/InfoButton/InfoButton'
import Loader from '../../components/Loader/Loader'
import './DashboardHome.css'

function DashboardHome() {
  const { user, loading: authLoading } = useAuth()
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  })
  const [checklistStats, setChecklistStats] = useState({
    completed: 0,
    pending: 0,
    thisMonth: 0
  })
  const [budgetStats, setBudgetStats] = useState({
    spent: 0,
    remaining: 0,
    total: 0,
    percentage: 0
  })
  const [rsvpStats, setRSVPStats] = useState({
    confirmed: 0,
    total: 0
  })
  const [timelineStats, setTimelineStats] = useState({
    configured: false,
    percentage: 0
  })
  const [totalTasks, setTotalTasks] = useState(0)
  const [dataLoading, setDataLoading] = useState(true)

  // Cargar datos del dashboard desde Supabase
  useEffect(() => {
    if (!user) {
      // Si no hay usuario, asegurarse de que el loader se oculte
      setDataLoading(false)
      return
    }

    const loadDashboardData = async () => {
      try {
        setDataLoading(true)
        const data = await getDashboardData(user.id)
        
        setTimeLeft(data.countdown)
        setChecklistStats(data.checklist)
        setBudgetStats(data.budget)
        setRSVPStats(data.rsvp)
        setTimelineStats(data.timeline)
        setTotalTasks(data.totalTasks)
      } catch (error) {
        console.error('Error loading dashboard data:', error)
      } finally {
        // Reducido delay para mejor rendimiento
        setTimeout(() => {
          setDataLoading(false)
        }, 50)
      }
    }

    loadDashboardData()
  }, [user])

  // Actualizar countdown cada segundo - calculando desde la fecha de boda para mayor precisión
  useEffect(() => {
    if (!user || dataLoading) return

    let intervalId = null
    let weddingDate = null

    // Función helper para obtener wedding_id (similar a dashboardService)
    const getUserWeddingId = async (userId) => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('partner_user_id')
          .eq('id', userId)
          .maybeSingle()

        const weddingOwnerId = profile?.partner_user_id || userId

        const { data: wedding } = await supabase
          .from('weddings')
          .select('id')
          .eq('user_id', weddingOwnerId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        return wedding?.id || null
      } catch (error) {
        console.error('Error getting wedding ID:', error)
        return null
      }
    }

    // Obtener fecha de boda una vez y almacenarla
    const initializeCountdown = async () => {
      try {
        const weddingId = await getUserWeddingId(user.id)
        if (!weddingId) {
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
          return
        }

        const { data, error } = await supabase
          .from('weddings')
          .select('event_date')
          .eq('id', weddingId)
          .single()

        if (error || !data?.event_date) {
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
          return
        }

        // Almacenar la fecha de la boda - asegurar que incluya la hora correctamente
        weddingDate = new Date(data.event_date)
        // Si la fecha no tiene hora, establecerla a medianoche del día del evento
        if (data.event_date && !data.event_date.includes('T')) {
          weddingDate.setHours(0, 0, 0, 0)
        }
        
        // Calcular countdown desde la fecha de boda (siempre preciso)
        const calculateTimeLeft = () => {
          if (!weddingDate) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 }
          }

          const now = new Date()
          const diff = weddingDate.getTime() - now.getTime()

          if (diff <= 0) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0 }
          }

          // Calcular correctamente días, horas, minutos y segundos
          const totalSeconds = Math.floor(diff / 1000)
          const days = Math.floor(totalSeconds / (60 * 60 * 24))
          const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60))
          const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)
          const seconds = totalSeconds % 60

          return { days, hours, minutes, seconds }
        }

        // Establecer countdown inicial
        setTimeLeft(calculateTimeLeft())
        
        // Actualizar cada segundo recalculando desde la fecha de boda (más preciso)
        intervalId = setInterval(() => {
          setTimeLeft(calculateTimeLeft())
        }, 1000)
      } catch (error) {
        console.error('Error initializing countdown:', error)
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
      }
    }

    initializeCountdown()

    // Limpiar intervalo cuando el componente se desmonte o cambie el usuario
    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [user, dataLoading])

  // Mostrar loader mientras se cargan los datos o mientras se verifica la autenticación
  if (authLoading || dataLoading) {
    return <Loader />
  }

  return (
    <div className="dashboard-home">
      <div className="dashboard-header">
        <h1 className="dashboard-title">
          EVENT DASHBOARD
          <InfoButton
            title="EVENT DASHBOARD"
            content={
              <div>
                <p>
                  Your wedding is coming together. Here's a quick overview of your planning progress.
                </p>
                <p>
                  <strong>What you'll find here:</strong>
                </p>
                <ul>
                  <li><strong>Countdown:</strong> See exactly how much time you have until your big day</li>
                  <li><strong>Checklist:</strong> Track completed and pending tasks</li>
                  <li><strong>Budget:</strong> Monitor your spending and remaining budget</li>
                  <li><strong>RSVP:</strong> See how many guests have confirmed</li>
                </ul>
                <p>
                  Use the sidebar to navigate to different sections and manage every aspect of your wedding planning.
                </p>
              </div>
            }
          />
        </h1>
      </div>

      {/* Top Row Summary Cards */}
      <div className="dashboard-summary-cards">
        {/* Countdown Card */}
        <div className="summary-card countdown-card">
          <h2 className="summary-card-title">Countdown</h2>
          <div className="countdown-display">
            <div className="countdown-item">
              <span className="countdown-number">{dataLoading ? '...' : timeLeft.days}</span>
              <span className="countdown-label">Days</span>
            </div>
            <div className="countdown-item">
              <span className="countdown-number">{dataLoading ? '...' : timeLeft.hours}</span>
              <span className="countdown-label">Hours</span>
            </div>
            <div className="countdown-item">
              <span className="countdown-number">{dataLoading ? '...' : timeLeft.minutes}</span>
              <span className="countdown-label">Min</span>
            </div>
            <div className="countdown-item">
              <span className="countdown-number">{dataLoading ? '...' : timeLeft.seconds}</span>
              <span className="countdown-label">Sec</span>
            </div>
          </div>
        </div>

        {/* Checklist Summary Card */}
        <div className="summary-card checklist-summary-card">
          <h2 className="summary-card-title">Checklist</h2>
          <div className="summary-card-content">
            <div className="summary-stat-item">
              <span className="summary-stat-label">Completed tasks:</span>
              <span className="summary-stat-value">{dataLoading ? '...' : checklistStats.completed}</span>
            </div>
            <div className="summary-stat-item">
              <span className="summary-stat-label">Pending tasks:</span>
              <span className="summary-stat-value">{dataLoading ? '...' : checklistStats.pending}</span>
            </div>
            <div className="summary-stat-item">
              <span className="summary-stat-label">Tasks this month:</span>
              <span className="summary-stat-value">{dataLoading ? '...' : checklistStats.thisMonth}</span>
            </div>
          </div>
          <Link to="/dashboard/checklist" className="summary-card-link">
            View checklist →
          </Link>
        </div>

        {/* Budget Summary Card */}
        <div className="summary-card budget-summary-card">
          <h2 className="summary-card-title">Budget</h2>
          <div className="summary-card-content">
            <div className="summary-budget-progress">
              <div className="summary-circular-progress">
                <svg className="summary-progress-ring" width="100" height="100" viewBox="0 0 100 100">
                  <circle
                    className="summary-progress-ring-background"
                    stroke="var(--color-cremita)"
                    strokeWidth="6"
                    fill="transparent"
                    r="42"
                    cx="50"
                    cy="50"
                  />
                  <circle
                    className="summary-progress-ring-progress"
                    stroke="var(--color-dorado)"
                    strokeWidth="6"
                    fill="transparent"
                    r="42"
                    cx="50"
                    cy="50"
                    strokeDasharray={`${2 * Math.PI * 42}`}
                    strokeDashoffset={`${2 * Math.PI * 42 * (1 - (budgetStats.percentage / 100))}`}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div className="summary-progress-percentage">{dataLoading ? '...' : `${budgetStats.percentage}%`}</div>
              </div>
            </div>
            <div className="summary-budget-stats">
              <div className="summary-stat-item">
                <span className="summary-stat-label">Spent:</span>
                <span className="summary-stat-value">
                  {dataLoading ? '...' : `$${budgetStats.spent.toLocaleString()}`}
                </span>
              </div>
              <div className="summary-stat-item">
                <span className="summary-stat-label">Remaining:</span>
                <span className="summary-stat-value">
                  {dataLoading ? '...' : `$${budgetStats.remaining.toLocaleString()}`}
                </span>
              </div>
            </div>
          </div>
          <Link to="/dashboard/budget-manager" className="summary-card-link">
            View budget →
          </Link>
        </div>

        {/* RSVP Summary Card */}
        <div className="summary-card rsvp-summary-card">
          <h2 className="summary-card-title">RSVP</h2>
          <div className="summary-card-content">
            <div className="summary-stat-item">
              <span className="summary-stat-label">Confirmed:</span>
              <span className="summary-stat-value">{dataLoading ? '...' : rsvpStats.confirmed}</span>
            </div>
            <div className="summary-stat-item">
              <span className="summary-stat-label">Total guests:</span>
              <span className="summary-stat-value">{dataLoading ? '...' : rsvpStats.total}</span>
            </div>
          </div>
          <Link to="/dashboard/guest-manager" className="summary-card-link">
            View guests →
          </Link>
        </div>
      </div>

      {/* Progress Overview Section */}
      <div className="progress-overview-section">
        <h2 className="progress-overview-title">Progress Overview</h2>
        <div className="progress-bars">
          {/* Tasks completed */}
          <div className="progress-bar-item">
            <div className="progress-bar-header">
              <span className="progress-bar-label">Tasks completed</span>
              <span className="progress-bar-percentage">
                {dataLoading ? '...' : checklistStats.completed + checklistStats.pending > 0 
                  ? Math.round((checklistStats.completed / (checklistStats.completed + checklistStats.pending)) * 100)
                  : 0}%
              </span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill"
                style={{
                  width: dataLoading ? '0%' : checklistStats.completed + checklistStats.pending > 0
                    ? `${(checklistStats.completed / (checklistStats.completed + checklistStats.pending)) * 100}%`
                    : '0%'
                }}
              ></div>
            </div>
            <div className="progress-bar-value">
              {dataLoading ? '...' : `${checklistStats.completed} / ${checklistStats.completed + checklistStats.pending} tasks completed`}
            </div>
          </div>

          {/* Budget spent */}
          <div className="progress-bar-item">
            <div className="progress-bar-header">
              <span className="progress-bar-label">Budget spent</span>
              <span className="progress-bar-percentage">{dataLoading ? '...' : `${budgetStats.percentage}%`}</span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill"
                style={{ width: dataLoading ? '0%' : `${budgetStats.percentage}%` }}
              ></div>
            </div>
            <div className="progress-bar-value">
              {dataLoading ? '...' : `$${budgetStats.spent.toLocaleString()} / $${budgetStats.total.toLocaleString()}`}
            </div>
          </div>

          {/* Guests confirmed */}
          <div className="progress-bar-item">
            <div className="progress-bar-header">
              <span className="progress-bar-label">Guests confirmed</span>
              <span className="progress-bar-percentage">
                {dataLoading ? '...' : rsvpStats.total > 0 
                  ? Math.round((rsvpStats.confirmed / rsvpStats.total) * 100)
                  : 0}%
              </span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill"
                style={{
                  width: dataLoading ? '0%' : rsvpStats.total > 0
                    ? `${(rsvpStats.confirmed / rsvpStats.total) * 100}%`
                    : '0%'
                }}
              ></div>
            </div>
            <div className="progress-bar-value">
              {dataLoading ? '...' : `${rsvpStats.confirmed} / ${rsvpStats.total} guests confirmed`}
            </div>
          </div>

        </div>
      </div>

      {/* Angels Image */}
      <div className="dashboard-angels-container">
        <img 
          src="/img/AngelesConChanpagne.png" 
          alt="Angels with Champagne" 
          className="dashboard-angels-image"
        />
      </div>
    </div>
  )
}

export default DashboardHome
